import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../style/About.css';

const About = () => {
    const timelineRef = useRef(null);
    const lineRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!timelineRef.current || !lineRef.current) return;

            const timeline = timelineRef.current;
            const line = lineRef.current;
            const timelineRect = timeline.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const dots = timeline.querySelectorAll('.timeline-dot');
            if (dots.length === 0) return;

            const firstDot = dots[0];
            const lastDot = dots[dots.length - 1];

            // Calculate start and end positions relative to the timeline container
            // Using the center of the dots for precise alignment
            const firstDotRect = firstDot.getBoundingClientRect();
            const lastDotRect = lastDot.getBoundingClientRect();

            const firstDotCenter = (firstDotRect.top + firstDotRect.height / 2) - timelineRect.top;
            const lastDotCenter = (lastDotRect.top + lastDotRect.height / 2) - timelineRect.top;

            const maxLineHeight = lastDotCenter - firstDotCenter;

            // Set the starting position of the line
            line.style.top = `${firstDotCenter}px`;

            // Calculate height based on scroll position
            // Trigger point: Center of viewport
            const triggerPoint = windowHeight / 2;

            // Current draw position relative to timeline top
            let drawPoint = triggerPoint - timelineRect.top;

            // Adjust to be relative to the line start (first dot)
            let relativeDrawLine = drawPoint - firstDotCenter;

            // Clamp
            if (relativeDrawLine < 0) relativeDrawLine = 0;
            if (relativeDrawLine > maxLineHeight) relativeDrawLine = maxLineHeight;

            line.style.height = `${relativeDrawLine}px`;

            // Active State Logic
            // The line end position in the timeline container coordinates is:
            const currentLineEnd = firstDotCenter + relativeDrawLine;

            dots.forEach((dot) => {
                const dotRect = dot.getBoundingClientRect();
                const dotCenter = (dotRect.top + dotRect.height / 2) - timelineRect.top;

                // Activate if the line has reached the dot's center (with small buffer)
                if (currentLineEnd >= dotCenter - 10) {
                    dot.classList.add('active');
                } else {
                    dot.classList.remove('active');
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        handleScroll(); // Initial measure

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    return (
        <div className="about-container">
            {/* Hero Section */}
            <div className="about-hero">
                <div className="about-hero-content">
                    <h1>
                        <span className="engineering">Engineering</span>
                        <span className="the-future">The Future</span>
                    </h1>
                    <p className="subtitle">
                        A chronicle of innovation, technical mastery, and strategic growth in the global enterprise landscape.
                    </p>
                </div>
            </div>

            {/* Journey Section */}
            <div id="our-journey" className="about-journey-section">
                <div className="section-header">
                    <span className="section-label">THE CHRONICLE</span>
                    <h2>Our Journey</h2>
                </div>

                <div className="timeline-container" ref={timelineRef}>
                    <div className="timeline-scroll-line" ref={lineRef}></div>
                    {/* Timeline Item 1 */}
                    <div className="timeline-item right">
                        <div className="timeline-year">2012</div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>KovanTech Founded</h3>
                            <p>Established in Houston, Texas with a vision to deliver high-quality IT consulting and build long-term client partnerships.
</p>
                        </div>
                    </div>

                    {/* Timeline Item 2 */}
                    <div className="timeline-item left">
                        <div className="timeline-year">2017</div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>North America Expansion & Cloud Launch</h3>
                            <p>Expanded operations across North America and established an offshore delivery center in Coimbatore, India, enabling scalable and cost-effective delivery. Launched Cloud Services and strengthened capabilities in SAP and Oracle ERP solutions, supporting enterprise modernization initiatives.</p>
                        </div>
                    </div>

                    {/* Timeline Item 3 */}
                    <div className="timeline-item right">
                        <div className="timeline-year">2019</div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>Nearshore Expansion & New Capabilities</h3>
                            <p>Expanded into a nearshore delivery model with operations in Costa Rica and Mexico, enhancing scalability, cost efficiency, and regional support. Launched ServiceNow and Cyber Security practices, addressing growing enterprise demand for automation, resilience, and security.</p>
                        </div>
                    </div>

                    {/* Timeline Item 4 */}
                    <div className="timeline-item left">
                        <div className="timeline-year">PRESENT</div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>Digital Transformation Partner</h3>
                            <p>Positioned as a trusted Digital Transformation Partner, delivering integrated solutions across AI & Data, Cloud, Enterprise Applications (SAP & Oracle), ServiceNow, and Cyber Security.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="about-cta">
                <div className="cta-content">
                    <h1>Join the Legacy</h1>
                    <p>Supporting enterprise clients with scalable, secure, and future-ready technology solutions.
</p>
                    <div className="cta-buttons">
                        <Link to="/contact" className="btn-get-started">Get Started</Link>
                        <Link to="/services" className="btn-learn-more">Learn More</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;
